import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";

import { Button, Checkbox, Line, ModalBody, ModalHeader, P, toast } from "@sorcery/ui";

import { getVariantTitle } from "@sorcery/utils/universal/variant";
import { useModal } from "~/providers/ModalProvider";
import { collectionlistJotai } from "~/state/collectionJotai";
import type { CardType, VariantType } from "~/types/card";
import type { CollectionItemDTOType } from "~/types/collection";
import { SessionStorageKeys } from "~/types/storage";
import { api } from "~/utils/trpc";

export const CollectionDeleteCardsModal = ({
  card,
  variant,
}: {
  card: CardType;
  variant?: VariantType;
}) => {
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const collectionlist = useAtomValue(collectionlistJotai)!;
  const [error, setError] = useState("");
  const { closeModal } = useModal();
  const [dontAsk, setDontAsk] = useState(false);
  const trpcUtils = api.useUtils();

  const itemsToDelete = variant
    ? (collectionlist
        .find((i) => i.card.id === card.id)
        ?.groupings.find((g) => g.variantId === variant.id)?.items ?? [])
    : (collectionlist
        .find((i) => i.card.id === card.id)
        ?.groupings.reduce(
          (acc, grouping) => acc.concat(grouping.items),
          [] as CollectionItemDTOType[],
        ) ?? []);

  const { mutateAsync: deleteItems, error: trpcERrror } = api.collection.deleteItems.useMutation({
    onSuccess() {
      toast("Collection updated");

      void trpcUtils.collection.getCollectionlistBySession.invalidate();
      void trpcUtils.collection.search.invalidate();
      closeModal();
    },
  });

  const onSubmit = () => {
    const payload: Record<string, string> = {};
    if (variant) {
      payload.variantId = variant.id;
    } else {
      payload.cardId = card.id;
    }
    if (dontAsk) {
      // biome-ignore lint/style/noRestrictedGlobals: <explanation>
      sessionStorage.setItem(SessionStorageKeys.DONTASKDELETE, dontAsk.toString());
    }
    void deleteItems(payload);
  };

  // Track any trpc errors for display
  useEffect(() => {
    if (trpcERrror) {
      // don't expose server side error messages
      setError("Something went wrong.");
    }
  }, [trpcERrror]);

  return (
    <div className="p-6">
      {/* header */}
      <ModalHeader title="Bulk Delete" />

      <ModalBody>
        {/* item */}
        <div className="mb-2 w-full">
          <P>
            <strong>Delete {itemsToDelete.length} cards </strong>
            <span>from your collection?</span>
          </P>
          <div className="curiosa-quote">
            {card.name}
            <ul
              className={`mb-6 ml-4 max-h-[120px] list-outside list-disc overflow-hidden ${
                itemsToDelete.length > 5 && "gradient-mask-b-70"
              }`}
            >
              {itemsToDelete.map((i) => (
                <li key={i.id}>
                  {getVariantTitle(
                    // biome-ignore lint/style/noNonNullAssertion: <explanation>
                    card.variants.find((v) => v.id === i.variantId)!,
                  )}{" "}
                  <span>-&nbsp;</span>
                  {i.condition.toUpperCase()}
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-2 flex items-center space-x-2" onClick={() => setDontAsk(!dontAsk)}>
            <Checkbox id="dontask" checked={dontAsk} label={"Don't ask again"} />
          </div>
          <P>
            This will skip the confirmation popup when bulk deleting, and will last for the duration
            of this session on Curiosa.
          </P>

          {error && (
            <div className="text-red my-4" role="alert">
              <p>
                <span className="font-bold">Error. </span>
                {error}
                <span>.</span>
              </p>
            </div>
          )}
        </div>
      </ModalBody>

      {/* footer */}
      <Line />
      <div className="flex flex-row-reverse pt-4 gap-2">
        <Button variant="destructive" onClick={onSubmit}>
          Confirm Delete
        </Button>
        <Button onClick={closeModal} variant="secondary">
          Cancel
        </Button>
      </div>
    </div>
  );
};
